import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Sparkles, CheckCircle2, XCircle, RefreshCw, AlertTriangle, Crown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGeneratePractice, type GeneratedQuestion, type GeneratePracticeInput } from "@/hooks/useGeneratePractice";
import { useAIQuota } from "@/hooks/useAIQuota";
import { PaywallDialog } from "./PaywallDialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";

interface AIPracticeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: GeneratePracticeInput;
}

interface AnswerState {
  selected?: string;
  submitted?: boolean;
}

export function AIPracticeDialog({ open, onOpenChange, context }: AIPracticeDialogProps) {
  const user = useUser();
  const { quiz_remaining, quiz_limit, isUnlimited } = useAIQuota();
  const [questions, setQuestions] = useState<GeneratedQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<number, AnswerState>>({});
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());
  const [paywallOpen, setPaywallOpen] = useState(false);
  const generate = useGeneratePractice();

  const persistAttempt = async (q: GeneratedQuestion, state: AnswerState, key: string) => {
    if (!user?.id || savedKeys.has(key)) return;
    setSavedKeys((prev) => new Set(prev).add(key));
    const { error } = await supabase.from("ai_practice_attempts").insert({
      user_id: user.id,
      category: context.category,
      module_title: context.moduleTitle ?? null,
      original_question: context.originalQuestion ?? null,
      question: q.question,
      options: q.options as any,
      correct_answer: q.correct_answer,
      user_answer: state.selected ?? null,
      is_correct: !!state.selected && state.selected === q.correct_answer,
      explanation: q.explanation ?? null,
      answered: !!state.submitted,
    });
    if (error) {
      setSavedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      console.error("[AIPractice] failed to save attempt", error);
    }
  };

  const flushUnsaved = async () => {
    if (!user?.id || questions.length === 0) return;
    await Promise.all(
      questions.map((q, idx) => {
        const key = q.question;
        if (savedKeys.has(key)) return Promise.resolve();
        return persistAttempt(q, answers[idx] ?? {}, key);
      }),
    );
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      // fire-and-forget para não atrasar fechamento
      void flushUnsaved();
    }
    onOpenChange(next);
  };

  const handleGenerate = async () => {
    await flushUnsaved();
    generate.mutate(context, {
      onSuccess: (data) => {
        setQuestions(data.questions);
        setAnswers({});
        setSavedKeys(new Set());
      },
      onError: (err) => {
        if (err.kind === "quota_exceeded") {
          setPaywallOpen(true);
          return;
        }
        toast.error(err.message);
      },
    });
  };

  useEffect(() => {
    if (open && questions.length === 0 && !generate.isPending && !generate.isError) {
      handleGenerate();
    }
    if (!open) {
      setQuestions([]);
      setAnswers({});
      setSavedKeys(new Set());
      generate.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const select = (qIdx: number, optId: string) => {
    setAnswers((prev) => {
      if (prev[qIdx]?.submitted) return prev;
      return { ...prev, [qIdx]: { ...prev[qIdx], selected: optId } };
    });
  };

  const submit = (qIdx: number) => {
    const q = questions[qIdx];
    setAnswers((prev) => {
      const newState: AnswerState = { ...prev[qIdx], submitted: true };
      if (q) void persistAttempt(q, newState, q.question);
      return { ...prev, [qIdx]: newState };
    });
  };

  const remainingLabel = isUnlimited
    ? "∞ hoje"
    : `${quiz_remaining} / ${quiz_limit} hoje`;

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Praticar mais — gerado por IA
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 flex-wrap">
              3 questões extras sobre o mesmo conceito da aula.
              <Badge variant="outline" className="text-[10px]">
                {remainingLabel}
              </Badge>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {generate.isPending && questions.length === 0 && (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <Card key={i}>
                    <CardContent className="pt-6 space-y-3">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
                <p className="text-center text-sm text-muted-foreground">
                  Gerando questões com IA...
                </p>
              </div>
            )}

            {generate.isError && questions.length === 0 && (
              <Card className="border-destructive/50">
                <CardContent className="pt-6 flex flex-col items-center text-center gap-3">
                  <AlertTriangle className="h-8 w-8 text-destructive" />
                  <p className="text-sm">{generate.error?.message ?? "Erro ao gerar questões"}</p>
                  <Button variant="outline" onClick={handleGenerate}>
                    Tentar novamente
                  </Button>
                </CardContent>
              </Card>
            )}

            {questions.map((q, qIdx) => {
              const state = answers[qIdx] ?? {};
              const submitted = !!state.submitted;
              const isCorrect = submitted && state.selected === q.correct_answer;
              return (
                <Card key={qIdx}>
                  <CardContent className="pt-6 space-y-3">
                    <div className="flex items-start gap-2">
                      <Badge variant="secondary" className="mt-0.5">{qIdx + 1}</Badge>
                      <p className="font-medium text-sm flex-1">{q.question}</p>
                    </div>

                    <RadioGroup
                      value={state.selected ?? ""}
                      onValueChange={(v) => select(qIdx, v)}
                      className="space-y-2"
                    >
                      {q.options.map((opt) => {
                        const isThis = state.selected === opt.id;
                        const isRight = opt.id === q.correct_answer;
                        let cls = "border-border";
                        if (submitted) {
                          if (isRight) cls = "border-emerald-500 bg-emerald-500/5";
                          else if (isThis && !isRight) cls = "border-red-500 bg-red-500/5";
                        }
                        return (
                          <Label
                            key={opt.id}
                            className={cn(
                              "flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors text-sm",
                              !submitted && "hover:bg-accent",
                              submitted && "cursor-default",
                              cls
                            )}
                          >
                            <RadioGroupItem value={opt.id} disabled={submitted} />
                            <span className="flex-1">{opt.text}</span>
                            {submitted && isRight && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                            {submitted && isThis && !isRight && <XCircle className="h-4 w-4 text-red-500" />}
                          </Label>
                        );
                      })}
                    </RadioGroup>

                    {!submitted && (
                      <Button
                        size="sm"
                        onClick={() => submit(qIdx)}
                        disabled={!state.selected}
                        className="w-full"
                      >
                        Confirmar
                      </Button>
                    )}

                    {submitted && (
                      <div
                        className={cn(
                          "p-3 rounded-lg border text-sm",
                          isCorrect
                            ? "bg-emerald-500/5 border-emerald-500/20"
                            : "bg-red-500/5 border-red-500/20"
                        )}
                      >
                        <div className="flex items-center gap-2 mb-1 font-semibold">
                          {isCorrect ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Correto!
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 text-red-500" /> Incorreto
                            </>
                          )}
                        </div>
                        <p className="text-muted-foreground">{q.explanation}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {questions.length > 0 && (
            <div className="flex items-center justify-between pt-3 border-t">
              <span className="text-xs text-muted-foreground">{remainingLabel}</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => handleClose(false)}>
                  Fechar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={generate.isPending || (!isUnlimited && quiz_remaining <= 0)}
                  className="gap-1"
                >
                  {generate.isPending ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      Gerando...
                    </>
                  ) : !isUnlimited && quiz_remaining <= 0 ? (
                    <>
                      <Crown className="h-3.5 w-3.5" />
                      Sem cota hoje
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      Gerar mais 3
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} feature="ai_practice" />
    </>
  );
}
